import { useEffect, useState } from 'react';
import { GAME_IDS, type AccountsConfig, type GameId } from '@gmp/shared-types/browser';
import { SERVER_URL } from '../net/config';

interface ServerConfigView {
  games: GameId[];
  /** Public VAPID key for turn alerts, or null when the server doesn't send them. */
  pushKey: string | null;
  /** Login methods this server has configured; null = accounts are off (sign-in is hidden). */
  accounts: AccountsConfig | null;
}

let cache: ServerConfigView | null = null;
let loading: Promise<ServerConfigView | null> | null = null;

/** The server's public settings (fetched once per page load). */
export function loadServerConfig(): Promise<ServerConfigView | null> {
  if (cache) return Promise.resolve(cache);
  loading ??= fetch(`${SERVER_URL}/api/config`)
    .then((r) => r.json())
    .then((c: { games: { id: GameId }[]; pushKey?: string | null; accounts?: AccountsConfig | null }) => (cache = {
      games: c.games.map((g) => g.id), pushKey: c.pushKey ?? null, accounts: c.accounts?.providers.length ? c.accounts : null,
    }))
    .catch(() => {
      loading = null;
      return null;
    });
  return loading;
}

/** Games this server lets rooms play (playable plus feature-flagged beta). */
export function useEnabledGames(): GameId[] {
  const [games, setGames] = useState<GameId[]>(cache?.games ?? GAME_IDS.filter((g) => g === 'panic-button' || g === 'snapscramble' || g === 'act-of-nonsense'));
  useEffect(() => {
    if (cache) return;
    void loadServerConfig().then((c) => c && setGames(c.games));
  }, []);
  return games;
}

/** Configured login methods (null while loading or when accounts are off). */
export function useAccountsConfig(): AccountsConfig | null {
  const [accounts, setAccounts] = useState<AccountsConfig | null>(cache?.accounts ?? null);
  useEffect(() => {
    if (cache) return;
    void loadServerConfig().then((c) => c && setAccounts(c.accounts));
  }, []);
  return accounts;
}
